export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

export function formatDateLabel(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Compact date for mobile history cards e.g. Sat, May 16, 2026 */
export function formatDateCompact(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDateLong(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Parse Odoo/API datetimes for display.
 * Odoo stores UTC but often returns naive strings like "2026-05-16 12:12:00".
 * Without a timezone, `new Date("...T12:12:00")` is treated as local time and shifts the clock.
 */
export function parseOdooDateTime(dateTimeStr) {
  if (!dateTimeStr) return null;
  const trimmed = String(dateTimeStr).trim();
  if (!trimmed || trimmed === 'false') return null;

  if (/[zZ]$/.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed) || /[+-]\d{4}$/.test(trimmed)) {
    const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const d = new Date(`${trimmed}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const normalized = trimmed.replace(' ', 'T');
  const withSeconds = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(normalized);
  const iso = withSeconds ? normalized : `${normalized}:00`;
  const d = new Date(`${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Short time for stat cards e.g. 09:57 AM */
export function formatTimeShort(dateTimeStr) {
  const d = parseOdooDateTime(dateTimeStr);
  if (!d) return '—';
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatDateTime(dateTimeStr) {
  const d = parseOdooDateTime(dateTimeStr);
  if (!d) return '—';
  return d.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatHours(hours) {
  if (hours === null || hours === undefined) return '—';
  const n = Number(hours);
  if (Number.isNaN(n)) return '—';
  return `${n.toFixed(1)}h`;
}

/** True when the day is marked absent (past no-show or missed check-out). */
export function isAttendanceAbsent(attendance) {
  if (!attendance) return false;
  if (attendance.is_absent === true) return true;
  return (attendance.attendance_status || '').toLowerCase() === 'absent';
}

export function getStatusMeta(status) {
  const key = (status || '').toLowerCase();
  const map = {
    early: {
      label: 'Early',
      chipClass: 'ea-chip--early',
      className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    },
    normal: {
      label: 'On time',
      chipClass: 'ea-chip--normal',
      className: 'bg-blue-100 text-blue-800 border-blue-200',
    },
    late: {
      label: 'Late',
      chipClass: 'ea-chip--late',
      className: 'bg-amber-100 text-amber-800 border-amber-200',
    },
    absent: {
      label: 'Absent',
      chipClass: 'ea-chip--absent',
      className: 'bg-slate-100 text-slate-700 border-slate-200',
    },
    false: {
      label: 'Not checked in',
      chipClass: 'ea-chip--none',
      className: 'bg-gray-100 text-gray-600 border-gray-200',
    },
  };
  return map[key] || {
    label: status || '—',
    chipClass: 'ea-chip--none',
    className: 'bg-gray-100 text-gray-700 border-gray-200',
  };
}

export function getStatusChipClass(status) {
  return getStatusMeta(status).chipClass;
}

export function defaultDateRange(days = 30) {
  const dateTo = new Date();
  const dateFrom = new Date();
  dateFrom.setDate(dateFrom.getDate() - days);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { date_from: fmt(dateFrom), date_to: fmt(dateTo) };
}
